import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Users, Store, Clock, Tag, Calendar, Flag, Check, X, Trash2, Plus, KeyRound, Copy, Pencil } from "lucide-react";
import { PLACEHOLDER_IMAGE } from "@/lib/data";
import { ImageUpload } from "@/components/ImageUpload";
import { DescriptionEditor } from "@/components/DescriptionEditor";
import { BusinessForm } from "@/pages/BusinessDashboard";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [cats, setCats] = useState([]);
  const [reports, setReports] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [daily, setDaily] = useState([]);
  const [newDaily, setNewDaily] = useState({ text: "", emoji: "✨" });
  const [deals, setDeals] = useState([]);
  const [events, setEvents] = useState([]);
  const [cityForm, setCityForm] = useState({ title: "", organizer: "", date: "", time: "", location: "", price: 0, image: "", website: "", description: "", font_family: "", font_size: 16, letter_spacing: 0 });
  const [showAddBiz, setShowAddBiz] = useState(false);
  const [editBiz, setEditBiz] = useState(null);
  const [editEventId, setEditEventId] = useState(null);
  const emptyCityForm = { title: "", organizer: "", date: "", time: "", location: "", price: 0, image: "", website: "", description: "", font_family: "", font_size: 16, letter_spacing: 0 };
  const resetCityForm = () => { setCityForm(emptyCityForm); setEditEventId(null); };
  const startEditEvent = (e) => {
    setEditEventId(e.id);
    setCityForm({ title: e.title || "", organizer: e.organizer || "", date: e.date || "", time: e.time || "", location: e.location || "", price: e.price || 0, image: e.image || "", website: e.website || "", description: e.description || "", font_family: e.font_family || "", font_size: e.font_size || 16, letter_spacing: e.letter_spacing || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  const loadAll = async () => {
    const [s, b, u, c, r, d, dl, ev] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/businesses"), api.get("/admin/users"),
      api.get("/categories"), api.get("/admin/reports"), api.get("/daily"),
      api.get("/deals"), api.get("/events"),
    ]);
    setStats(s.data); setBusinesses(b.data); setUsers(u.data); setCats(c.data); setReports(r.data); setDaily(d.data);
    setDeals(dl.data); setEvents(ev.data);
  };
  useEffect(() => { if (user?.role === "admin") loadAll(); /* eslint-disable-next-line */ }, [user]);

  if (!user || user.role !== "admin") return null;

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); loadAll(); } catch (e) { toast.error(errMsg(e.response?.data?.detail)); } };

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 min-h-[70vh]">
      <h1 className="font-display text-4xl font-semibold tracking-tight">Admin Dashboard</h1>
      <p className="text-slate-500 mt-1 mb-6">Manage the Voyes platform.</p>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
        {[["Users", stats?.users, Users], ["Businesses", stats?.businesses, Store], ["Pending", stats?.pending, Clock], ["Deals", stats?.deals, Tag], ["Events", stats?.events, Calendar], ["Reports", stats?.reports, Flag]].map(([l, v, Icon]) => (
          <div key={l} className="bg-white rounded-2xl border border-slate-100 p-4" data-testid={`stat-${l.toLowerCase()}`}>
            <Icon className="w-5 h-5 text-orange-500 mb-1" />
            <p className="text-2xl font-display font-semibold">{v ?? 0}</p>
            <p className="text-xs text-slate-500">{l}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="businesses">
        <TabsList className="rounded-full bg-[#F3F0EA] p-1">
          <TabsTrigger value="businesses" data-testid="admin-tab-businesses" className="rounded-full">Businesses</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full">Users</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories" className="rounded-full">Categories</TabsTrigger>
          <TabsTrigger value="reports" data-testid="admin-tab-reports" className="rounded-full">Reports</TabsTrigger>
          <TabsTrigger value="posts" data-testid="admin-tab-posts" className="rounded-full">Posts</TabsTrigger>
          <TabsTrigger value="cityevents" data-testid="admin-tab-cityevents" className="rounded-full">Calgary Events</TabsTrigger>
          <TabsTrigger value="daily" data-testid="admin-tab-daily" className="rounded-full">Daily Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="businesses" className="mt-6 space-y-3">
          {editBiz ? (
            <div className="bg-blue-50/50 rounded-3xl border border-blue-100 p-2" data-testid="admin-edit-biz-panel">
              <div className="flex items-center justify-between px-4 pt-2">
                <h3 className="font-display text-lg font-semibold">Edit “{editBiz.name}”</h3>
                <Button variant="ghost" className="rounded-full" data-testid="admin-cancel-edit-biz" onClick={() => setEditBiz(null)}><X className="w-4 h-4 mr-1" /> Close</Button>
              </div>
              <BusinessForm admin existing={editBiz} onSaved={() => { setEditBiz(null); loadAll(); }} />
            </div>
          ) : showAddBiz ? (
            <div className="bg-orange-50/50 rounded-3xl border border-orange-100 p-2">
              <div className="flex items-center justify-between px-4 pt-2">
                <h3 className="font-display text-lg font-semibold">Add a new business</h3>
                <Button variant="ghost" className="rounded-full" data-testid="admin-cancel-add-biz" onClick={() => setShowAddBiz(false)}><X className="w-4 h-4 mr-1" /> Close</Button>
              </div>
              <BusinessForm admin createEndpoint="/admin/businesses" onSaved={() => { setShowAddBiz(false); loadAll(); }} />
            </div>
          ) : (
            <Button data-testid="admin-add-biz-btn" onClick={() => setShowAddBiz(true)} className="rounded-full bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1" /> Add business</Button>
          )}
          {businesses.map((b) => (
            <div key={b.id} data-testid={`admin-biz-${b.id}`} className="bg-white rounded-2xl border border-slate-100 p-4">
              <div className="flex items-center gap-4">
                <img src={b.logo || PLACEHOLDER_IMAGE} alt="" className="w-12 h-12 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{b.name}</p>
                  <p className="text-sm text-slate-500 truncate">{b.address}</p>
                </div>
                <Badge className={`rounded-full ${b.status === "approved" ? "bg-green-100 text-green-700" : b.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{b.status}</Badge>
                <Button size="sm" variant="outline" data-testid={`edit-biz-${b.id}`} onClick={() => { setEditBiz(b); setShowAddBiz(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="rounded-full"><Pencil className="w-4 h-4" /></Button>
                {b.status !== "approved" && <Button size="sm" data-testid={`approve-${b.id}`} onClick={() => act(() => api.post(`/admin/businesses/${b.id}/approve`), "Approved")} className="rounded-full bg-green-600 hover:bg-green-700"><Check className="w-4 h-4" /></Button>}
                {b.status !== "rejected" && <Button size="sm" variant="outline" data-testid={`reject-${b.id}`} onClick={() => act(() => api.post(`/admin/businesses/${b.id}/reject`), "Rejected")} className="rounded-full"><X className="w-4 h-4" /></Button>}
                <Button size="sm" variant="outline" data-testid={`delete-biz-${b.id}`} onClick={() => act(() => api.delete(`/admin/businesses/${b.id}`), "Deleted")} className="rounded-full text-red-500"><Trash2 className="w-4 h-4" /></Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <KeyRound className="w-4 h-4 text-slate-400" />
                {b.claim_code ? (
                  <>
                    <span className="text-xs text-slate-500">Claim code:</span>
                    <code data-testid={`claim-code-${b.id}`} className="bg-orange-50 text-orange-700 font-mono font-semibold rounded-lg px-2.5 py-1 text-sm tracking-widest">{b.claim_code}</code>
                    <button data-testid={`copy-code-${b.id}`} onClick={() => { navigator.clipboard?.writeText(b.claim_code); toast.success("Claim code copied"); }} className="text-slate-400 hover:text-orange-500 p-1"><Copy className="w-4 h-4" /></button>
                    <span className="text-xs text-slate-400">— give this to the client to manage this page</span>
                  </>
                ) : (
                  <span className="text-xs text-slate-400">Owned/claimed.</span>
                )}
                <Button size="sm" variant="ghost" data-testid={`gen-code-${b.id}`} onClick={() => act(() => api.post(`/admin/businesses/${b.id}/claim-code`), "New claim code generated")} className="rounded-full text-xs h-7 ml-auto">{b.claim_code ? "Regenerate" : "Generate claim code"}</Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="mt-6 space-y-2">
          {users.map((u) => (
            <div key={u.id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4">
              <div className="flex-1"><p className="font-semibold">{u.name}</p><p className="text-sm text-slate-500">{u.email}</p></div>
              <Badge className="rounded-full bg-[#F3F0EA] text-slate-700">{u.role}</Badge>
              {u.role !== "admin" && <Button size="sm" variant="outline" data-testid={`del-user-${u.id}`} onClick={() => act(() => api.delete(`/admin/users/${u.id}`), "User removed")} className="rounded-full text-red-500"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <div className="flex gap-2 mb-4 max-w-md">
            <Input data-testid="new-cat-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="rounded-full" />
            <Button data-testid="add-cat-btn" onClick={() => { if (newCat) act(() => api.post("/categories", { name: newCat }), "Category added").then(() => setNewCat("")); }} className="rounded-full bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center gap-2 bg-white border border-slate-100 rounded-full pl-4 pr-2 py-1.5">
                <span className="text-sm">{c.name}</span>
                <button data-testid={`del-cat-${c.slug}`} onClick={() => act(() => api.delete(`/categories/${c.id}`), "Category removed")} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-3">
          {reports.length === 0 ? <p className="text-slate-400">No reported content.</p> : reports.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4">
              <Flag className="w-5 h-5 text-red-500" />
              <div className="flex-1"><p className="font-semibold capitalize">{r.content_type} reported</p><p className="text-sm text-slate-500">{r.reason}</p></div>
              <Badge className="rounded-full">{r.status}</Badge>
              {r.status === "open" && <Button size="sm" onClick={() => act(() => api.post(`/admin/reports/${r.id}/resolve`), "Resolved")} className="rounded-full bg-green-600">Resolve</Button>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="posts" className="mt-6">
          <p className="text-slate-500 text-sm mb-4">Review and remove any deal or event that isn't appropriate. Removed posts are deleted immediately.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Tag className="w-5 h-5 text-orange-500" /> Deals ({deals.length})</h3>
              <div className="space-y-2">
                {deals.length === 0 ? <p className="text-slate-400 text-sm">No deals.</p> : deals.map((d) => (
                  <div key={d.id} data-testid={`admin-deal-${d.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
                    <img src={d.business?.logo || PLACEHOLDER_IMAGE} alt="" className="w-10 h-10 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{d.title}</p>
                      <p className="text-xs text-slate-500 truncate">{d.business?.name} · {d.discount}</p>
                    </div>
                    <button data-testid={`admin-del-deal-${d.id}`} onClick={() => act(() => api.delete(`/deals/${d.id}`), "Deal removed")} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-orange-500" /> Events ({events.length})</h3>
              <div className="space-y-2">
                {events.length === 0 ? <p className="text-slate-400 text-sm">No events.</p> : events.map((e) => (
                  <div key={e.id} data-testid={`admin-event-${e.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
                    <img src={e.image || e.business?.logo || PLACEHOLDER_IMAGE} alt="" className="w-10 h-10 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{e.title}</p>
                      <p className="text-xs text-slate-500 truncate">{e.business?.name} · {e.date}</p>
                    </div>
                    <button data-testid={`admin-del-event-${e.id}`} onClick={() => act(() => api.delete(`/events/${e.id}`), "Event removed")} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cityevents" className="mt-6">
          <p className="text-slate-500 text-sm mb-4">Post city-wide events happening in Calgary now. These appear on the Events page with a "Calgary Event" badge and aren't tied to any business.</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-3 self-start">
              <h3 className="font-display text-lg font-semibold flex items-center gap-2">{editEventId ? <><Pencil className="w-5 h-5" /> Edit Calgary event</> : <><Plus className="w-5 h-5" /> New Calgary event</>}</h3>
              <Input data-testid="cityevent-title" placeholder="Event title" value={cityForm.title} onChange={(e) => setCityForm({ ...cityForm, title: e.target.value })} className="rounded-xl" />
              <Input data-testid="cityevent-organizer" placeholder="Organizer / host (e.g. City of Calgary)" value={cityForm.organizer} onChange={(e) => setCityForm({ ...cityForm, organizer: e.target.value })} className="rounded-xl" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="cityevent-date" type="date" value={cityForm.date} onChange={(e) => setCityForm({ ...cityForm, date: e.target.value })} className="rounded-xl" />
                <Input data-testid="cityevent-time" type="time" value={cityForm.time} onChange={(e) => setCityForm({ ...cityForm, time: e.target.value })} className="rounded-xl" />
              </div>
              <Input data-testid="cityevent-location" placeholder="Location / venue" value={cityForm.location} onChange={(e) => setCityForm({ ...cityForm, location: e.target.value })} className="rounded-xl" />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="cityevent-price" type="number" placeholder="Price (0 = free)" value={cityForm.price} onChange={(e) => setCityForm({ ...cityForm, price: e.target.value })} className="rounded-xl" />
                <Input data-testid="cityevent-website" placeholder="Website / ticket link" value={cityForm.website} onChange={(e) => setCityForm({ ...cityForm, website: e.target.value })} className="rounded-xl" />
              </div>
              <ImageUpload label="Event image" testid="cityevent-image" value={cityForm.image} onChange={(url) => setCityForm({ ...cityForm, image: url })} previewClass="h-32" />
              <DescriptionEditor testid="cityevent-desc" value={cityForm} onChange={(patch) => setCityForm({ ...cityForm, ...patch })} />
              <div className="flex gap-2">
                <Button data-testid="cityevent-add" onClick={() => {
                  if (!cityForm.title.trim()) return toast.error("Title required");
                  const payload = { ...cityForm, price: Number(cityForm.price) || 0 };
                  if (editEventId) {
                    act(() => api.put(`/events/${editEventId}`, payload), "Calgary event updated").then(resetCityForm);
                  } else {
                    act(() => api.post("/admin/events", payload), "Calgary event posted").then(resetCityForm);
                  }
                }} className="rounded-full bg-orange-500 hover:bg-orange-600 flex-1">{editEventId ? "Update event" : "Post event"}</Button>
                {editEventId && <Button data-testid="cityevent-cancel" variant="outline" onClick={resetCityForm} className="rounded-full">Cancel</Button>}
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="font-display text-lg font-semibold mb-1">Posted Calgary events</h3>
              {events.filter((e) => e.is_city_event).length === 0 ? <p className="text-slate-400 text-sm">No city events yet.</p> : events.filter((e) => e.is_city_event).map((e) => (
                <div key={e.id} data-testid={`cityevent-item-${e.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-3">
                  <img src={e.image || PLACEHOLDER_IMAGE} alt="" className="w-12 h-12 rounded-xl object-cover bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{e.title}</p>
                    <p className="text-xs text-slate-500 truncate">{e.date} · {e.location}{e.organizer ? ` · ${e.organizer}` : ""}</p>
                  </div>
                  <button data-testid={`edit-cityevent-${e.id}`} onClick={() => startEditEvent(e)} className="text-slate-400 hover:text-orange-500 p-1"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`del-cityevent-${e.id}`} onClick={() => act(() => api.delete(`/events/${e.id}`), "Event removed")} className="text-slate-400 hover:text-red-500 p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="daily" className="mt-6">
          <p className="text-slate-500 text-sm mb-4">Short, sweet daily encouragement shown on the homepage hero. One rotates in each day.</p>
          <div className="flex flex-col sm:flex-row gap-2 mb-6 max-w-2xl">
            <Input data-testid="daily-emoji-input" value={newDaily.emoji} onChange={(e) => setNewDaily({ ...newDaily, emoji: e.target.value })} placeholder="✨" className="rounded-full sm:w-20 text-center" maxLength={2} />
            <Input data-testid="daily-text-input" value={newDaily.text} onChange={(e) => setNewDaily({ ...newDaily, text: e.target.value })} placeholder="Write a short encouraging note…" className="rounded-full flex-1" />
            <Button data-testid="add-daily-btn" onClick={() => { if (newDaily.text.trim()) act(() => api.post("/daily", newDaily), "Note added").then(() => setNewDaily({ text: "", emoji: "✨" })); }} className="rounded-full bg-orange-500 hover:bg-orange-600"><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-2">
            {daily.length === 0 ? <p className="text-slate-400">No notes yet.</p> : daily.map((m) => (
              <div key={m.id} data-testid={`daily-item-${m.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4">
                <span className="text-2xl">{m.emoji}</span>
                <p className="flex-1 text-slate-700 italic">{m.text}</p>
                <button data-testid={`del-daily-${m.id}`} onClick={() => act(() => api.delete(`/daily/${m.id}`), "Note removed")} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
